import { Text } from '@macarongfactory/web-ui'

import type { ReactNode } from 'react'

interface IFuelGaugeProps {
  /** 마이클 오너 실측 평균 실연비 */
  realFuelEconomy: number
  /** 공인연비 (게이지 최댓값) */
  officialFuelEconomy: number
  /** 연비 단위 (km/L | km/kWh) */
  unit: string
  /** 중앙 라벨 (실연비 | 실전비) */
  realLabel: string
  /** 상단 상태 버블 아이콘 (디자인 시스템 이모지 아이콘) — 미지정 시 버블 미노출 */
  statusIcon?: ReactNode
  /** 상단 상태 버블 라벨 (예: '양호해요') — 공인연비 없으면 미지정(버블 미노출, Figma 7547:9523) */
  statusLabel?: string
  /** 상태별 색상 (아크 채움·중앙 수치) — 훌륭 초록 / 양호 파랑 / 저조 빨강 */
  statusColor: string
  /**
   * 잠금 화면(B) 오버레이 — 지정하면 게이지 영역(아크·눈금·수치)을 흐림 처리하고 위에 겹친다.
   * 상태 버블은 흐리지 않는다 (Figma 10259:25934, Layer blur 10 = CSS blur 5px).
   */
  lockOverlay?: ReactNode
}

/** 아크 SVG 기하 (Figma arcData 기준) */
const VIEW = 164
const CENTER = VIEW / 2
const RADIUS = 75
const STROKE = 14
/** 시작/끝 각도(rad) — 하단 약 100° 갭, 시계방향 약 260° 아크 */
const START_ANGLE = 2.4521875381469727
const END_ANGLE = 6.976080894470215
/** 하단 갭만큼 높이를 줄여 빈 영역 제거 */
const GAUGE_BOX_HEIGHT = 140
const TRACK_COLOR = '#04283E'
const TRACK_OPACITY = 0.11

const polar = (angle: number) => ({
  x: CENTER + RADIUS * Math.cos(angle),
  y: CENTER + RADIUS * Math.sin(angle),
})

/** from→to(시계방향) 아크 path */
const arcPath = (from: number, to: number) => {
  const start = polar(from)
  const end = polar(to)
  const largeArc = to - from > Math.PI ? 1 : 0
  return `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${end.x} ${end.y}`
}

/**
 * 실연비 반원형 아크 게이지 (Figma `Gauge Container` 기준)
 * - 트랙(공인연비 100% 기준) 위에 실연비/공인연비 비율만큼 브랜드색으로 채운다.
 */
const FuelGauge = ({
  realFuelEconomy,
  officialFuelEconomy,
  unit,
  realLabel,
  statusIcon,
  statusLabel,
  statusColor,
  lockOverlay,
}: IFuelGaugeProps) => {
  const ratio =
    officialFuelEconomy > 0
      ? Math.min(realFuelEconomy / officialFuelEconomy, 1)
      : // 공인연비 없으면 실연비 자체가 최대 눈금 → 풀 게이지 (Figma 7547:9523 공인전비=No)
        1
  const fillEnd = START_ANGLE + ratio * (END_ANGLE - START_ANGLE)
  /** 우측 끝 눈금 — 공인연비, 없으면 실연비 */
  const maxTickValue =
    officialFuelEconomy > 0 ? officialFuelEconomy : realFuelEconomy

  return (
    <div className='flex flex-col items-center gap-dimension-x2'>
      {/* 상태 버블 (다크 핀 + 하단 화살표) — 공인연비 없으면 미노출 */}
      {statusLabel ? (
        <div className='flex flex-col items-center'>
          <div className='flex items-center gap-dimension-x1 rounded-[10px] bg-background-neutral-inverse py-dimension-x2 pl-dimension-x2 pr-dimension-x3'>
            <span className='inline-flex' aria-hidden>
              {statusIcon}
            </span>
            <Text
              variant='body-1-medium'
              color='text-foreground-neutral-inverse'
            >
              {statusLabel}
            </Text>
          </div>
          <span
            aria-hidden
            className='h-dimension-x2 w-dimension-x2 -translate-y-1/2 rotate-45 bg-background-neutral-inverse'
          />
        </div>
      ) : null}

      {/* 0 — 아크 + 중앙 값 — 공인연비 (라벨이 아크 끝을 감싸도록 가깝게) */}
      {/* 잠금 화면(B): Figma Gauge Result Container(10259:25934) — 높이 152 영역 전체를
        lock overlay가 덮고, 흐림 게이지는 상단 11px 아래에 둔다(위쪽 흐림 번짐이 overlay 안에 머문다) */}
      <div
        className={
          lockOverlay ? 'relative h-[152px] w-full' : 'relative w-full'
        }
      >
        <div
          className={`flex w-full items-end justify-center gap-dimension-x2 ${
            lockOverlay ? 'absolute inset-x-0 top-[11px] blur-[5px]' : ''
          }`}
          aria-hidden={lockOverlay ? true : undefined}
        >
          <Text
            variant='caption-1-medium'
            color='text-foreground-neutral-assistive'
          >
            0{unit}
          </Text>
          <div
            className='relative shrink-0'
            style={{ width: VIEW, height: GAUGE_BOX_HEIGHT }}
            role='img'
            aria-label={
              officialFuelEconomy > 0
                ? `${realLabel} ${realFuelEconomy}${unit}, 공인연비 ${officialFuelEconomy}${unit} 대비 게이지`
                : `${realLabel} ${realFuelEconomy}${unit} 게이지`
            }
          >
            <svg
              width={VIEW}
              height={GAUGE_BOX_HEIGHT}
              viewBox={`0 0 ${VIEW} ${GAUGE_BOX_HEIGHT}`}
              fill='none'
              aria-hidden
            >
              <path
                d={arcPath(START_ANGLE, END_ANGLE)}
                stroke={TRACK_COLOR}
                strokeOpacity={TRACK_OPACITY}
                strokeWidth={STROKE}
                strokeLinecap='round'
              />
              <path
                d={arcPath(START_ANGLE, fillEnd)}
                stroke={statusColor}
                strokeWidth={STROKE}
                strokeLinecap='round'
              />
            </svg>
            {/* 중앙 값 — 원 중심(y=82)에 맞춰 정렬 */}
            <div className='absolute inset-x-0 top-[82px] flex -translate-y-1/2 flex-col items-center gap-dimension-x0-5'>
              <Text variant='body-1-semibold' color={statusColor}>
                {realLabel}
              </Text>
              <span
                className='text-[40px] font-semibold leading-none'
                style={{ color: statusColor }}
              >
                {realFuelEconomy}
              </span>
              <Text variant='body-1-medium' color={statusColor}>
                {unit}
              </Text>
            </div>
          </div>
          <Text
            variant='caption-1-medium'
            color='text-foreground-neutral-assistive'
          >
            {maxTickValue}
            {unit}
          </Text>
        </div>
        {lockOverlay}
      </div>
    </div>
  )
}

export default FuelGauge
